import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const WebLayout = ({ children }) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItems = [
        { path: '/student-benefit', label: '대학생 혜택' },
        { path: '/promotion-partner', label: '홍보 파트너' },
        { path: '/store-promotion', label: '우리가게 홍보' },
        { path: '/download', label: '다운로드' }
    ];

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsMobileMenuOpen(false); // 모바일 메뉴 닫기
    };

    // 페이지 전환 시 자동으로 최상단으로 스크롤
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            {/* 상단 네비게이션 */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* 로고 */}
                        <Link
                            to="/"
                            className="flex items-center space-x-2 hover:opacity-50 transition-opacity"
                            onClick={scrollToTop}
                        >
                            <div className="h-16 w-auto">
                                <img
                                    src="/images/bapfriend_banner02.png"
                                    alt="밥친구 배너"
                                    className="h-full"
                                />
                            </div>
                        </Link>

                        {/* 네비게이션 메뉴 */}
                        <nav className="hidden md:flex items-center space-x-8">
                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        currentPath === item.path
                                            ? 'text-amber-600'
                                            : 'text-gray-700'
                                    }`}
                                    onClick={scrollToTop}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </nav>

                        {/* 모바일 메뉴 버튼 */}
                        <div className="md:hidden">
                            <button
                                className="text-gray-700 active:text-amber-600 p-2"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 모바일 메뉴 드롭다운 */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-gray-200 bg-white">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                                        currentPath === item.path
                                            ? 'text-amber-600 bg-amber-50'
                                            : 'text-gray-700 active:text-amber-600 active:bg-gray-50'
                                    }`}
                                    onClick={scrollToTop}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </header>

            {/* 메인 컨텐츠 */}
            <main className="flex-1">
                {children}
            </main>

            {/* 푸터 */}
            <footer className="bg-gray-50 border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* 로고 및 소개 */}
                        <div className="space-y-4">
                            <Link
                                to="/"
                                className="flex items-center space-x-2 hover:opacity-50 transition-opacity"
                                onClick={scrollToTop}
                            >
                                <div className="w-10 h-10 flex items-center justify-center">
                                    <img src="/images/bapfriend_character01.png" alt="밥친구 로고" className="w-6 h-6" />
                                </div>
                                <span className="text-xl font-bold text-gray-900">밥친구</span>
                            </Link>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                혼밥이 싫을 때<br/>
                                밥친구
                            </p>
                        </div>

                        {/* 빠른 링크 */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                빠른 링크
                            </h3>
                            <ul className="space-y-2">
                                {navItems.map((item) => (
                                    <li key={item.path}>
                                        <Link
                                            to={item.path}
                                            className="text-gray-600 hover:text-amber-600 text-sm transition-colors"
                                            onClick={scrollToTop}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 사업자 정보 */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                사업자 정보
                            </h3>
                            <div className="text-sm text-gray-600 space-y-1">
                                <p>주식회사 싱귤 (SINGULE Corp.)</p>
                                <p>사업자등록번호: 558-87-03429</p>
                                <p>이메일: singulecorp@gmail.com</p>
                            </div>
                        </div>
                    </div>

                    {/* 하단 저작권 */}
                    <div className="mt-8 pt-8 border-t border-gray-200">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="flex space-x-6 mt-4 md:mt-0">
                                <Link
                                    to="/privacy"
                                    className="text-sm text-gray-500 hover:text-amber-600"
                                    onClick={scrollToTop}
                                >
                                    개인정보처리방침
                                </Link>
                                <Link
                                    to="/terms"
                                    className="text-sm text-gray-500 hover:text-amber-600"
                                    onClick={scrollToTop}
                                >
                                    이용약관
                                </Link>
                                <a
                                    href="https://github.com/kraftenty"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-gray-500 hover:text-amber-600"
                                    onClick={scrollToTop}
                                >
                                    개발자 정보
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default WebLayout;
